/**
 * What happens after a case is routed out.
 *
 * The engine routes out and never guesses (prohibition 4), and that does not
 * change. What changes is the destination. Today all nine reasons get the same
 * referral to gov.uk, which treats "you have a child on the application" — an
 * ordinary case the engine simply does not encode yet — the same as "your
 * partner cannot sponsor anyone", which is a genuine no. They are not the same
 * and should not read the same.
 *
 * WHY THIS IS NOT IN THE RULESET. `packages/rules` encodes Appendix FM. Whether
 * a registered adviser is willing to take a case on is a commercial and service
 * question, not an Immigration Rules one, and putting it in a dated ruleset
 * would mean a pricing decision needed a rules version and an adviser's
 * sign-off (F9, F11). ADR-0011 keeps rules out of code; this keeps commerce out
 * of the rules.
 *
 * WHY IT IS NOT IN THE ENGINE EITHER. The engine is the auditable kernel. A
 * regulator reading it should find determination logic and nothing about what
 * Verity would like to sell.
 *
 * WHY A PACKAGE. The checker, the workspace and the emailed copy all have to
 * say the same thing to the same person. Three implementations of "can an
 * adviser help with this?" is three chances to tell someone the wrong answer
 * about their own case.
 */
import type { OutOfScopeFlag } from "@verity/engine";

export type PathwayGroup = "not_yet_encoded" | "needs_judgement" | "not_available";

export interface Pathway {
  group: PathwayGroup;
  /** Whether a registered adviser can take this on at all. */
  adviserCanHelp: boolean;
  heading: string;
  /**
   * What VERITY adds, and nothing more.
   *
   * The ruleset's own `referral_hint` already says who can help and, in some
   * cases, something more valuable than anything here — the adequate-
   * maintenance hint tells someone this is usually better news than the income
   * threshold, which is the single most reassuring sentence in the product.
   * That copy is adviser-owned (F11) and is still rendered. This adds only the
   * part the rules cannot know: that we would carry their case across.
   */
  explanation: string;
  /** The one thing to offer. Never a menu — this is already a disappointing moment. */
  action: { kind: "adviser_enquiry" | "referral"; label: string };
}

/**
 * The nine reasons, in three groups.
 *
 * 1 · NOT YET ENCODED — ordinary applications the engine does not cover. A
 *     child on the application, self-employment income, adequate maintenance,
 *     the fiancé(e) route, the ten-year route. None of these is hard; they are
 *     roadmap gaps (Categories F/G are already Phase 4 in the plan). An adviser
 *     handles them today, and each one encoded later converts adviser hours
 *     into margin.
 *
 * 2 · NEEDS JUDGEMENT — a previous refusal, overstaying, a criminal record.
 *     These turn on what happened and how it is presented, not on arithmetic,
 *     and the engine most likely never covers them. Adviser-led from the first
 *     minute, and where the professional liability actually sits.
 *
 * 3 · NOT AVAILABLE — the sponsor holds none of the qualifying statuses. There
 *     is no partner visa here, whatever anyone charges. Nothing may be sold.
 */
const GROUPS: Record<OutOfScopeFlag, PathwayGroup> = {
  dependent_children: "not_yet_encoded",
  self_employed_income: "not_yet_encoded",
  adequate_maintenance_route: "not_yet_encoded",
  fiance_or_proposed_civil_partner: "not_yet_encoded",
  ten_year_route: "not_yet_encoded",

  previous_refusal: "needs_judgement",
  overstay: "needs_judgement",
  criminal_record: "needs_judgement",

  sponsor_not_eligible_to_sponsor: "not_available",
};

/**
 * Most restrictive wins.
 *
 * Someone can be flagged for more than one reason, and the combination decides
 * the destination rather than whichever the engine listed first. A case with a
 * child AND a previous refusal is a judgement case, not a roadmap gap — and if
 * the sponsor cannot sponsor, nothing else about the case matters, because
 * there is no application to make.
 */
const SEVERITY: Record<PathwayGroup, number> = {
  not_yet_encoded: 1,
  needs_judgement: 2,
  not_available: 3,
};

const PATHWAYS: Record<PathwayGroup, Omit<Pathway, "group">> = {
  not_yet_encoded: {
    adviserCanHelp: true,
    heading: "This is outside what Verity checks automatically",
    explanation:
      "There is nothing unusual about your case — this is a part of the rules Verity does not work through on its own yet. Everything you have already told us goes with you, so you would not be starting again.",
    action: { kind: "adviser_enquiry", label: "Talk to an adviser about your case" },
  },
  needs_judgement: {
    adviserCanHelp: true,
    heading: "This needs a person rather than a calculation",
    explanation:
      "What matters here is what happened and how it is explained, which is a judgement rather than a sum, so Verity does not attempt an answer. Everything you have already told us goes with you, so you would not be starting again.",
    action: { kind: "adviser_enquiry", label: "Talk to an adviser about your case" },
  },
  not_available: {
    adviserCanHelp: false,
    heading: "This route is not available on these answers",
    // Deliberately short. The ruleset's hint already says there may be another
    // route and that an adviser can say which, which is both more useful and
    // more hopeful than anything that belongs here.
    explanation: "We would rather tell you now than take you further.",
    action: { kind: "referral", label: "Find a registered adviser on the IAA register" },
  },
};

/** Where a routed-out case should go next. `null` if it was not routed out. */
export function pathwayFor(flags: readonly OutOfScopeFlag[]): Pathway | null {
  if (flags.length === 0) return null;

  let group: PathwayGroup = "not_yet_encoded";
  for (const flag of flags) {
    const candidate = GROUPS[flag];
    // An unknown flag must never quietly become the friendliest outcome. If a
    // tenth reason is added to the engine and not classified here, treat it as
    // needing judgement until someone decides otherwise.
    const resolved: PathwayGroup = candidate ?? "needs_judgement";
    if (SEVERITY[resolved] > SEVERITY[group]) group = resolved;
  }

  return { group, ...PATHWAYS[group] };
}

/** For tests and for anyone checking the classification is exhaustive. */
export function groupOf(flag: OutOfScopeFlag): PathwayGroup {
  return GROUPS[flag] ?? "needs_judgement";
}

export const ALL_FLAGS = Object.keys(GROUPS) as OutOfScopeFlag[];
